import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { SortableProvider, SortableItems, SortableItem, DragHandle } from "../../components/admin/SortableList";
import SeoFields from "../../components/admin/SeoFields";

const empty = {
  title: "", department: "", location: "", employment_type: "Full-time", experience: "",
  description: "", requirements: "", is_active: true,
  slug: "", valid_through: "", salary_range: "",
  meta_title: "", meta_description: "", meta_keywords: "", og_image: "", canonical_url: "", robots: "", locale: "en-IN",
};

export default function Careers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/admin/careers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/careers/${editing.id}`, form);
      else await api.post("/admin/careers", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this job?")) return;
    try { await api.delete(`/admin/careers/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Careers"
        subtitle="Job openings published on the careers page."
        actions={
          <Button onClick={openNew} data-testid="add-career-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New role
          </Button>
        }
      />
      <div className="p-8">
        <SortableProvider items={items} setItems={setItems} reorderEndpoint="/admin/reorder/careers">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="careers-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-6 py-3">Title</th>
                  <th className="px-6 py-3">Dept</th>
                  <th className="px-6 py-3">Location</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Active</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {items.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-[#4B5563]">No openings yet.</td></tr>}
                <SortableItems items={items}>
                  {items.map((it) => (
                    <SortableItem key={it.id} id={it.id} as="tr" data-testid={`career-row-${it.id}`}>
                      {(handleProps) => (
                        <>
                          <td className="px-3 py-3"><DragHandle handleProps={handleProps} /></td>
                          <td className="px-6 py-3 font-medium text-[#1F2937]">{it.title}</td>
                          <td className="px-6 py-3 text-[#4B5563]">{it.department || "—"}</td>
                          <td className="px-6 py-3 text-[#4B5563]">{it.location || "—"}</td>
                          <td className="px-6 py-3 text-[#4B5563]">{it.employment_type || "—"}</td>
                          <td className="px-6 py-3">
                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${
                              it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"
                            }`}>{it.is_active ? "Open" : "Closed"}</span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                            <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                          </td>
                        </>
                      )}
                    </SortableItem>
                  ))}
                </SortableItems>
              </tbody>
            </table>
          </div>
        </SortableProvider>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="career-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit role" : "New role"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2"><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="career-title" /></div>
            <div className="space-y-2"><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="career-dept" /></div>
            <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="career-loc" /></div>
            <div className="space-y-2"><Label>Type</Label><Input value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} data-testid="career-type" /></div>
            <div className="space-y-2"><Label>Experience</Label><Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="e.g., 3-5 years" data-testid="career-exp" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Description</Label><Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="career-desc" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Requirements</Label><Textarea rows={4} value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} data-testid="career-req" /></div>
            <div className="flex items-center gap-3 md:col-span-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="career-active" />
              <Label>Active (visible on site)</Label>
            </div>
            <div className="space-y-2"><Label>Valid through (date)</Label><Input type="date" value={(form.valid_through || "").substring(0,10)} onChange={(e) => setForm({ ...form, valid_through: e.target.value })} data-testid="career-valid" /></div>
            <div className="space-y-2"><Label>Salary range</Label><Input value={form.salary_range} onChange={(e) => setForm({ ...form, salary_range: e.target.value })} placeholder="e.g., ₹6-10 LPA" data-testid="career-salary" /></div>
          </div>
          <SeoFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-career" className="bg-[#C2410C] hover:bg-[#9A3412]">Save role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
